import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, TriangleAlert } from 'lucide-react';
import { api } from '@/lib/api';
import { useConfirm } from '@/components/ui/confirm';
import { usePermission } from '@/Hooks/usePermission';
import { LOCALES } from '@/lib/i18n';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, Select, Textarea } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import {
  apiError,
  apiStatus,
  StorefrontPaymentPolicy,
  StorefrontStatus,
  type Storefront,
  type StorefrontDomain,
  type UpsertStorefrontInput,
} from './storefrontLib';

/** `en`/`ar` — the two locales this dashboard itself carries; the storefront's own `seo.byLocale` is not limited to these, but this is what the operator can author here. */
const SEO_LOCALES = Object.keys(LOCALES);

function toForm(s: Storefront): UpsertStorefrontInput {
  return {
    name: s.name,
    defaultLocale: s.defaultLocale,
    logoUrl: s.logoUrl ?? undefined,
    faviconUrl: s.faviconUrl ?? undefined,
    themeColor: s.themeColor ?? undefined,
    description: s.description ?? undefined,
    seo: { indexable: s.seo?.indexable ?? true, byLocale: { ...(s.seo?.byLocale ?? {}) } },
    analytics: { requireConsent: s.analytics?.requireConsent ?? true, providers: [...(s.analytics?.providers ?? [])] },
    paymentPolicy: s.paymentPolicy,
    scheduleHorizonDays: s.scheduleHorizonDays,
    abandonAfterMinutes: s.abandonAfterMinutes,
  };
}

/**
 * Branding, SEO, analytics and the commercial policy — the fields
 * `storefront.update` may write. `status` is deliberately not part of this
 * form's own save: the backend checks `storefront.publish` on the request
 * BODY, not the route, at the moment `status` is present, so it lives in its
 * own smaller control that only appears for someone who holds it.
 */
export default function BrandingTab() {
  const { t } = useTranslation();
  const { can } = usePermission();
  const confirmDialog = useConfirm();
  const canUpdate = can('storefront.update');
  const canPublish = can('storefront.publish');

  const [storefront, setStorefront] = useState<Storefront | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const [form, setForm] = useState<UpsertStorefrontInput>({});
  const [saving, setSaving] = useState(false);
  const [statusDraft, setStatusDraft] = useState<StorefrontStatus>(StorefrontStatus.DRAFT);
  const [publishing, setPublishing] = useState(false);

  const [domains, setDomains] = useState<StorefrontDomain[] | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    setNotFound(false);
    api
      .get<Storefront>('/storefront/admin')
      .then(({ data }) => {
        setStorefront(data);
        setForm(toForm(data));
        setStatusDraft(data.status);
      })
      .catch((e) => {
        if (apiStatus(e) === 404) setNotFound(true);
        else setError(apiError(e, t('storefront.branding.loadFailed')));
      })
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(load, [load]);

  useEffect(() => {
    if (!can('storefront.manage_domains')) return;
    api
      .get<StorefrontDomain[]>('/storefront/admin/domains')
      .then(({ data }) => setDomains(data))
      .catch(() => setDomains(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storefront?.id]);

  const createStorefront = async () => {
    setSaving(true);
    try {
      await api.put('/storefront/admin', { name: t('storefront.branding.defaultName') });
      toast.success(t('storefront.branding.created'));
      load();
    } catch (e) {
      toast.error(apiError(e, t('storefront.branding.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put<Storefront>('/storefront/admin', form);
      setStorefront(data);
      setForm(toForm(data));
      toast.success(t('storefront.branding.saved'));
    } catch (e) {
      toast.error(apiError(e, t('storefront.branding.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async () => {
    if (!storefront || statusDraft === storefront.status) return;
    const goingLive = statusDraft === StorefrontStatus.PUBLISHED;
    const ok = await confirmDialog.confirm({
      title: t('storefront.branding.statusConfirmTitle', { status: t(`storefront.status.${statusDraft}`) }),
      body: t(
        goingLive ? 'storefront.branding.publishConfirmBody' : 'storefront.branding.statusConfirmBody',
      ),
      confirmLabel: t('common.confirm'),
      tone: goingLive ? 'default' : 'danger',
    });
    if (!ok) return;
    setPublishing(true);
    try {
      const { data } = await api.put<Storefront>('/storefront/admin', { status: statusDraft });
      setStorefront(data);
      toast.success(t('storefront.branding.statusUpdated'));
    } catch (e) {
      toast.error(apiError(e, t('storefront.branding.statusUpdateFailed')));
      setStatusDraft(storefront.status);
    } finally {
      setPublishing(false);
    }
  };

  if (loading) return <LoadingState label={t('common.loading')} />;
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (notFound) {
    return (
      <EmptyState
        title={t('storefront.branding.notFound')}
        hint={t('storefront.branding.notFoundHint')}
        action={
          canUpdate ? (
            <Button size="sm" disabled={saving} onClick={() => void createStorefront()}>
              <Plus className="h-4 w-4" />
              {t('storefront.branding.create')}
            </Button>
          ) : undefined
        }
      />
    );
  }
  if (!storefront) return null;

  const noVerifiedDomain = domains !== null && domains.every((d) => !d.verifiedAt);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex-row items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <CardTitle>{t('storefront.branding.status')}</CardTitle>
            <Badge tone={toneForStatus(storefront.status)}>{t(`storefront.status.${storefront.status}`)}</Badge>
          </div>
          {canPublish ? (
            <div className="flex items-center gap-2">
              <Select value={statusDraft} onChange={(e) => setStatusDraft(e.target.value as StorefrontStatus)}>
                {Object.values(StorefrontStatus).map((s) => (
                  <option key={s} value={s}>
                    {t(`storefront.status.${s}`)}
                  </option>
                ))}
              </Select>
              <Button
                size="sm"
                disabled={publishing || statusDraft === storefront.status}
                onClick={() => void updateStatus()}
              >
                {t('common.save')}
              </Button>
            </div>
          ) : (
            <span className="text-xs text-ink-muted">{t('storefront.branding.publishRestricted')}</span>
          )}
        </CardHeader>
        {storefront.status === StorefrontStatus.PUBLISHED && noVerifiedDomain && (
          <CardContent className="pt-0">
            <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{t('storefront.branding.noVerifiedDomainWarning')}</p>
            </div>
          </CardContent>
        )}
      </Card>

      <fieldset disabled={!canUpdate} className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>{t('storefront.branding.identity')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <Field label={t('storefront.branding.name')}>
              <Input value={form.name ?? ''} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </Field>
            <Field label={t('storefront.branding.defaultLocale')}>
              <Select
                value={form.defaultLocale ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, defaultLocale: e.target.value }))}
              >
                {SEO_LOCALES.map((l) => (
                  <option key={l} value={l}>
                    {LOCALES[l as keyof typeof LOCALES].nativeName}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('storefront.branding.logoUrl')}>
              <Input
                value={form.logoUrl ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, logoUrl: e.target.value }))}
              />
            </Field>
            <Field label={t('storefront.branding.faviconUrl')}>
              <Input
                value={form.faviconUrl ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, faviconUrl: e.target.value }))}
              />
            </Field>
            <Field label={t('storefront.branding.themeColor')} hint="#1B4332">
              <Input
                value={form.themeColor ?? ''}
                placeholder="#1B4332"
                onChange={(e) => setForm((f) => ({ ...f, themeColor: e.target.value }))}
              />
            </Field>
            <Field label={t('storefront.branding.paymentPolicy')}>
              <Select
                value={form.paymentPolicy ?? StorefrontPaymentPolicy.PAY_ON_PICKUP}
                onChange={(e) => setForm((f) => ({ ...f, paymentPolicy: e.target.value as StorefrontPaymentPolicy }))}
              >
                {Object.values(StorefrontPaymentPolicy).map((p) => (
                  <option key={p} value={p}>
                    {t(`storefront.paymentPolicy.${p}`)}
                  </option>
                ))}
              </Select>
            </Field>
            <div className="sm:col-span-2">
              <Field label={t('storefront.branding.description')}>
                <Textarea
                  rows={2}
                  value={form.description ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                />
              </Field>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('storefront.branding.seo')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.seo?.indexable ?? true}
                onChange={(e) =>
                  setForm((f) => ({ ...f, seo: { ...f.seo, indexable: e.target.checked } }))
                }
                className="h-4 w-4"
              />
              {t('storefront.branding.indexable')}
            </label>
            {SEO_LOCALES.map((locale) => {
              const entry = form.seo?.byLocale?.[locale] ?? {};
              const patch = (p: Partial<typeof entry>) =>
                setForm((f) => ({
                  ...f,
                  seo: {
                    indexable: f.seo?.indexable ?? true,
                    byLocale: { ...f.seo?.byLocale, [locale]: { ...entry, ...p } },
                  },
                }));
              return (
                <div key={locale} className="space-y-2 rounded-lg border p-3">
                  <p className="text-xs font-medium text-ink-muted">
                    {LOCALES[locale as keyof typeof LOCALES].nativeName}
                  </p>
                  <Field label={t('storefront.branding.seoTitle')}>
                    <Input maxLength={70} value={entry.title ?? ''} onChange={(e) => patch({ title: e.target.value })} />
                  </Field>
                  <Field label={t('storefront.branding.seoDescription')}>
                    <Textarea
                      rows={2}
                      maxLength={200}
                      value={entry.description ?? ''}
                      onChange={(e) => patch({ description: e.target.value })}
                    />
                  </Field>
                  <Field label={t('storefront.branding.seoImage')}>
                    <Input value={entry.imageUrl ?? ''} onChange={(e) => patch({ imageUrl: e.target.value })} />
                  </Field>
                </div>
              );
            })}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('storefront.branding.analytics')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.analytics?.requireConsent ?? true}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    analytics: {
                      requireConsent: e.target.checked,
                      providers: f.analytics?.providers ?? [],
                    },
                  }))
                }
                className="h-4 w-4"
              />
              {t('storefront.branding.requireConsent')}
            </label>
            <p className="text-xs text-ink-muted">
              {form.analytics?.providers?.length
                ? t('storefront.branding.providersCount', { count: form.analytics.providers.length })
                : t('storefront.branding.noProviders')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('storefront.branding.checkout')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <Field label={t('storefront.branding.scheduleHorizonDays')}>
              <Input
                type="number"
                min={0}
                max={60}
                value={form.scheduleHorizonDays ?? 7}
                onChange={(e) => setForm((f) => ({ ...f, scheduleHorizonDays: Number(e.target.value) }))}
              />
            </Field>
            <Field label={t('storefront.branding.abandonAfterMinutes')} hint={t('storefront.branding.abandonAfterMinutesHint')}>
              <Input
                type="number"
                min={5}
                max={1440}
                value={form.abandonAfterMinutes ?? 30}
                onChange={(e) => setForm((f) => ({ ...f, abandonAfterMinutes: Number(e.target.value) }))}
              />
            </Field>
            <div className="sm:col-span-2">
              <label className="flex items-center gap-2 text-sm text-ink-muted">
                <input type="checkbox" checked disabled className="h-4 w-4" />
                {t('storefront.branding.guestCheckoutLocked')}
              </label>
            </div>
          </CardContent>
        </Card>

        {canUpdate && (
          <div className="flex justify-end">
            <Button disabled={saving} onClick={() => void save()}>
              {t('common.save')}
            </Button>
          </div>
        )}
      </fieldset>
    </div>
  );
}
